import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, LayoutTemplate } from 'lucide-react';
import sitesApi from '../../services/sitesApi';
import { useToast } from '../../context/ToastContext';

export default function SiteTemplates() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('All');
    const navigate = useNavigate();
    const toast = useToast();

    useEffect(() => {
        // Fetch templates or use static list
        const fetchTemplates = async () => {
            try {
                // In case API is not ready, we can use mock data
                const res = await sitesApi.getTemplates().catch(() => ({ data: { templates: mockTemplates } }));
                setTemplates(res.data.templates || res.data || mockTemplates);
            } catch (err) {
                setTemplates(mockTemplates);
            } finally {
                setLoading(false);
            }
        };
        fetchTemplates();
    }, []);

    const mockTemplates = [
        { id: 't1', name: 'Fine Dining', category: 'Restaurant', premium: true, thumbnail: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&q=80&w=800' },
        { id: 't2', name: 'Cozy Cafe', category: 'Café', premium: false, thumbnail: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&q=80&w=800' },
        { id: 't3', name: 'Cloud Kitchen', category: 'Cloud Kitchen', premium: false, thumbnail: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&q=80&w=800' },
    ];

    const categories = ['All', 'Restaurant', 'Café', 'Catering', 'Cloud Kitchen'];

    const filteredTemplates = activeCategory === 'All' 
        ? templates 
        : templates.filter(t => t.category === activeCategory);

    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [showNameModal, setShowNameModal] = useState(false);
    const [siteName, setSiteName] = useState('');
    const [creating, setCreating] = useState(false);

    const handleSelectTemplate = (templateId) => {
        setSelectedTemplateId(templateId);
        setSiteName('');
        setShowNameModal(true);
    };

    const handleConfirmCreate = async (e) => {
        e.preventDefault();
        if (!siteName.trim()) return;
        setCreating(true);

        try {
            const res = await sitesApi.createSite({ name: siteName, template_id: selectedTemplateId });
            const newSite = res.data.site || res.data;
            setShowNameModal(false);
            navigate(`/sites/editor/${newSite.id || newSite._id}`);
        } catch (err) {
            toast.error('Failed to create site with template.');
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-300 pb-20">
            {/* Header */}
            <div className="bg-slate-900 border-b border-slate-800 py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors">
                        <ArrowLeft size={20} />
                        Back
                    </button>
                    <h1 className="text-3xl font-bold text-white mb-2">Choose a Template</h1>
                    <p className="text-slate-400">Start with a professionally designed template or build from scratch.</p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
                {/* Categories */}
                <div className="flex space-x-2 overflow-x-auto pb-4 mb-8 no-scrollbar">
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setActiveCategory(cat)}
                            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeCategory === cat ? 'bg-primary-600 text-white' : 'bg-slate-900 text-slate-400 hover:bg-slate-800'}`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Blank Template */}
                        {activeCategory === 'All' && (
                            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden hover:border-primary-500/50 transition-all group flex flex-col">
                                <div className="h-48 bg-slate-800 flex items-center justify-center">
                                    <LayoutTemplate size={48} className="text-slate-600 group-hover:text-primary-500 transition-colors" />
                                </div>
                                <div className="p-6 flex-1 flex flex-col">
                                    <h3 className="text-xl font-bold text-white mb-2">Blank Site</h3>
                                    <p className="text-slate-400 text-sm mb-6 flex-1">Start from scratch and build exactly what you want using our drag-and-drop builder.</p>
                                    <button onClick={() => handleSelectTemplate(null)} className="w-full py-2.5 bg-slate-800 hover:bg-primary-600 text-white rounded-xl font-medium transition-colors">
                                        Start from Blank
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Templates */}
                        {filteredTemplates.map(template => (
                            <div key={template.id} className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden hover:border-slate-700 transition-all group flex flex-col">
                                <div className="h-48 bg-slate-800 relative overflow-hidden">
                                    <img src={template.thumbnail} alt={template.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    {template.premium && (
                                        <div className="absolute top-4 right-4 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-md shadow-lg">
                                            PRO
                                        </div>
                                    )}
                                </div>
                                <div className="p-6 flex-1 flex flex-col">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="text-xl font-bold text-white">{template.name}</h3>
                                        <span className="text-xs bg-slate-800 text-slate-300 px-2 py-1 rounded-md">{template.category}</span>
                                    </div>
                                    <p className="text-slate-400 text-sm mb-6 flex-1">A beautiful template tailored for {template.category.toLowerCase()} businesses.</p>
                                    <button onClick={() => handleSelectTemplate(template.id)} className="w-full py-2.5 bg-slate-800 hover:bg-primary-600 text-white rounded-xl font-medium transition-colors">
                                        Use Template
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* New Site Modal */}
            {showNameModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-950/80 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 w-full max-w-md shadow-2xl">
                        <h3 className="text-xl font-bold text-white mb-4">Name your site</h3>
                        <form onSubmit={handleConfirmCreate}>
                            <div className="mb-6">
                                <label className="block text-sm text-slate-400 mb-2">Site Name</label>
                                <input
                                    type="text"
                                    autoFocus
                                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                                    placeholder="e.g. The Rustic Spoon"
                                    value={siteName}
                                    onChange={(e) => setSiteName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowNameModal(false)}
                                    className="flex-1 px-4 py-2.5 rounded-xl text-slate-300 hover:bg-slate-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={creating}
                                    className="flex-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                                >
                                    {creating ? 'Creating...' : 'Create Site'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
